import {RuleSeverityBadge} from '@github-ui/code-scanning-shared/components/RuleSeverityBadge'
// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-imports
import type {ActivityHeaderHeadingProps} from '@github-ui/commenting/ActivityHeader'
// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-imports
import {ActivityHeader, CommentAuthorTypes} from '@github-ui/commenting/ActivityHeader'
// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-imports
import {CommentActions} from '@github-ui/commenting/CommentActions'
// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-imports
import {VALUES} from '@github-ui/commenting/Values'
import {noop} from '@github-ui/noop'
import type {ReactionContent, ReactionViewerGroup} from '@github-ui/reaction-viewer/ReactionGroupsUtils'
import {ReactionViewerBase} from '@github-ui/reaction-viewer/ReactionViewerBase'
import type {SafeHTMLString} from '@github-ui/safe-html'
import {UnsafeHTMLText} from '@github-ui/safe-html/UnsafeHTML'
import {VerifiedHTMLText} from '@github-ui/safe-html/VerifiedHTML'
import {ssrSafeLocation, ssrSafeWindow} from '@github-ui/ssr-utils'
// eslint-disable-next-line no-restricted-imports
import {useToastContext} from '@github-ui/toast/ToastContext'
import {AlertIcon, StopIcon} from '@primer/octicons-react'
import {Flash} from '@primer/react'
import {useRef, useState} from 'react'
import {useCommentAnchor} from '../../hooks/use-comment-anchor'
import type {Comment} from '../../conversation-types'
import styles from './AutomatedReviewThreadComment.module.css'
import {AutomatedReviewSuggestion} from './AutomatedReview/AutomatedReviewSuggestion'
import {Blankslate} from '@primer/react/experimental'
import {AutomatedReviewFeedback} from './AutomatedReview/AutomatedReviewFeedback'
import {CopilotApplyChangesButton} from '@github-ui/copilot-apply-changes-button/CopilotApplyChangesButton'
import {useReactToCommentMutationWithCallback} from '@github-ui/pull-requests/use-react-to-comment-mutation'

type CommentState = 'editing' | 'hidden' | 'visible'

export interface AutomatedReviewThreadCommentProps {
  anchorPrefix?: string
  basePath: string
  comment: Comment
  /**
   * If true, the comment will scroll itself into view and show a blue border
   * when the component renders with a hash that matches the comment.
   */
  isAnchorable?: boolean
  isInDialogMode?: boolean | undefined
  isOutdated?: boolean
  onQuoteReply?: (quotedText?: string) => void
  originalDiffPathUri?: string | null
  /**
   * If true, the "Apply changes" button will be shown, allowing Copilot to
   * automatically apply suggested changes from the code review comment.
   * This is determined server-side based on feature flags, user permissions,
   * and whether the comment contains a suggestion.
   */
  showApplyChangesButton?: boolean
  showNegativeFeedbackModal?: boolean
  threadId: string
  threadPositionNumber?: number
  footnoteTextHTML?: string
  /**
   * If true, uses classic experience labels (e.g., "Commit suggestion" instead of "Apply suggestion").
   * This is set to true when rendered via the Rails react partial in the classic PR conversation view.
   */
  isClassicExperience?: boolean
}

function formatBotAuthorLogin(login?: string): string | undefined {
  if (!login) {
    return undefined
  }

  // Change the format of bots (eg.: `github-code-quality[bot]` to `github-code-quality`)
  return login.replace(/\[bot\]$/i, '')
}

export function AutomatedReviewThreadComment({
  anchorPrefix = 'r',
  basePath,
  comment,
  isAnchorable = false,
  isClassicExperience,
  isInDialogMode,
  isOutdated,
  onQuoteReply = noop,
  originalDiffPathUri,
  showApplyChangesButton = false,
  showNegativeFeedbackModal,
  threadId,
  threadPositionNumber,
  footnoteTextHTML,
}: AutomatedReviewThreadCommentProps): React.ReactElement {
  const {addToast} = useToastContext()
  const [isMinimized, setIsMinimized] = useState(comment.isHidden)
  const [visibilityErrorMessage] = useState('')
  const [reactionGroups, setReactionGroups] = useState<ReactionViewerGroup[] | undefined>(comment.reactionGroups)

  const commentBodyRef = useRef<HTMLDivElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const reactionButtonRef = useRef<HTMLButtonElement>(null)
  const {
    id: automatedCommentId,
    title,
    message,
    severity,
    suggestion,
    suggestionState,
    feedbackOptions,
    showFeedbackButtons: showFeedbackButtonsInput,
    showAiNote,
    source,
  } = comment.automatedComment || {}

  // this is needed because any properties coming from comment.automatedComment can be null
  // and cannot be used directly in AutomatedReviewFeedback
  const showFeedbackButtons = showFeedbackButtonsInput && automatedCommentId && feedbackOptions

  const showFootBar = showFeedbackButtons || showAiNote || footnoteTextHTML || reactionGroups

  const isCopilotAuthoredPr = source === 'copilot'

  const onReactSuccessCallback = (data: {reactionGroups: ReactionViewerGroup[]}) => {
    setReactionGroups(data.reactionGroups)
  }
  const {mutate: reactToCommentMutation} = useReactToCommentMutationWithCallback(basePath, onReactSuccessCallback)

  const onReact = (reaction: ReactionContent, viewerHasReacted: boolean) => {
    if (!comment.databaseId) throw new Error('Comment not found')

    setTimeout(() => reactionButtonRef.current?.focus(), 0)
    reactToCommentMutation(
      {
        commentDatabaseId: comment.databaseId,
        threadId,
        reaction,
        viewerHasReacted,
      },
      {
        onError: () => {
          // eslint-disable-next-line @github-ui/dotcom-primer/toast-migration
          addToast({
            type: 'error',
            message: 'Failed to react to comment',
          })
        },
      },
    )
  }

  // TODO(@aliceclv): figure out when the `.id` value gets populated to understand if it's a safe fallback
  // The unique identifier for this comment in the DOM
  const commentId = comment.databaseId?.toString() || comment.id
  // The anchor portion of the URL that will target this specific comment when navigating to it
  // This is used to scroll the comment into view and highlight it when directly linked
  const commentAnchor = commentId ? `${anchorPrefix}${commentId}` : undefined
  // Note(@aliceclv): Comment is not editable
  const commentState: CommentState = isMinimized ? 'hidden' : 'visible'

  // The full URL including anchor that points directly to this comment within the current page
  const pageScopedCommentHref =
    commentAnchor && ssrSafeLocation
      ? new URL(`${ssrSafeLocation.pathname}#${commentAnchor}`, ssrSafeLocation.origin).toString()
      : ''

  useCommentAnchor({
    isAnchorable,
    commentId,
    commentAnchor,
    containerRef,
  })

  const commentActivityProps = {
    headingProps: {as: 'h3'} as ActivityHeaderHeadingProps,
    avatarUrl: comment.author?.avatarUrl ?? VALUES.ghostUser.avatarUrl,
    comment: {
      ...comment,
      bodyForClipboard: comment.body,
      url: pageScopedCommentHref,
      referenceText: comment.reference.text ?? `#${comment.reference.number}`,
      // Note(@aliceclv): none of these actions are allowed in the context of an automated comment
      viewerCanMinimize: false,
      viewerCanUpdate: false,
      viewerCanDelete: false,
      viewerCanBlockFromOrg: false,
      viewerCanUnblockFromOrg: false,
      viewerCanReport: false,
      viewerCanReportToMaintainer: false,
      viewerCanReferenceInIssue: false,
      viewerCanQuoteReply: false,
    },
    commentRef: containerRef,
    editHistoryComponent: undefined,
    onBlock: undefined,
    onUnblock: undefined,
    onMinimize: setIsMinimized,
    onSuccessfulBlock: () => {
      //not ideal, but this is the easiest way to get all of the comments to reflect the newly updated state
      setTimeout(() => ssrSafeWindow?.location.reload(), 800)
    },
    isMinimized,
    navigate: noop,
    onReplySelect: onQuoteReply,
    isReply: false,
    forceInlineAvatar: true,
    containerStyle: {},
    originalDiffPathUri,
    isOutdated,
    // Comment actions are null as they are not allowed for automated comments
    editComment: () => null,
    deleteComment: () => null,
    hideComment: () => null,
    unhideComment: () => null,

    // Comment author props
    commentAuthorSlug: undefined,
    commentAuthorType: CommentAuthorTypes.Bot,
    commentAuthorLogin: formatBotAuthorLogin(comment.author?.login) ?? VALUES.ghostUser.login,
    hideProfileLink: true,
  }

  const getCommentAriaLabel = (): string => {
    if (threadPositionNumber !== undefined) {
      return `Comment ${threadPositionNumber}`
    } else {
      return 'Comment'
    }
  }

  return (
    <div
      ref={containerRef}
      id={isAnchorable ? commentAnchor : undefined}
      className={styles.automatedComment}
      style={{scrollMarginTop: isAnchorable ? '120px' : 0}}
      data-first-thread-comment
      data-marker-navigation-comment-thread-id={threadId}
      data-marker-navigation-comment-id={comment.id}
      data-testid="automated-review-comment"
      // When aria-label and role is set while still in gridcell mode, the browser doesn't the respect the
      // aria-hidden"true" and we end up with the aria-labels leaking into the accessible name of the gridcell,
      // which is not what we want. So we should only set these attributes while in dialog mode.
      {...(isInDialogMode
        ? {
            role: 'document',
            'aria-roledescription': 'comment',
            'aria-label': getCommentAriaLabel(),
          }
        : {})}
    >
      <div className={styles.automatedComment__headerContainer}>
        <ActivityHeader
          {...commentActivityProps}
          commentBody={comment.body}
          isInDialogMode={isInDialogMode}
          actions={<CommentActions {...commentActivityProps} />}
          badges={severity && <RuleSeverityBadge severity={severity} />}
          isOutdated={isOutdated}
        />
      </div>
      {visibilityErrorMessage && (
        <Flash variant="danger" className="m-3">
          <StopIcon className="mr-2" />
          {visibilityErrorMessage}
        </Flash>
      )}
      <div className={styles.automatedComment__bodyContainer}>
        {commentState === 'visible' && (
          <div className={styles.automatedComment__body}>
            <div className={styles.automatedComment__content}>
              {title && (
                <div className="d-flex flex-items-center">
                  <h3 className={styles.automatedComment__title}>{title}</h3>
                </div>
              )}
              <VerifiedHTMLText
                ref={commentBodyRef}
                className={`${styles.automatedComment__message} markdown-body`}
                comment-testid={`Comment message html for automated comment ${commentId}`}
                /* eslint-disable-next-line @github-ui/github-monorepo/no-cast-as-safe-html-string -- HTML is sanitized server-side. TODO: Move type assertion to data ingress point. */
                html={message as SafeHTMLString}
              />
            </div>
            {automatedCommentId && (
              <AutomatedReviewSuggestion
                commentTitle={title}
                commentAuthorLogin={comment.author?.login}
                feedbackOptions={feedbackOptions}
                isClassicExperience={isClassicExperience}
                suggestion={suggestion}
                suggestionState={suggestionState}
                automatedCommentId={automatedCommentId}
                basePath={basePath}
              />
            )}
            {showApplyChangesButton && (
              <CopilotApplyChangesButton
                commentPermalink={comment.url}
                isCopilotAuthoredPr={isCopilotAuthoredPr}
                commentCount={1}
              />
            )}
            {showFootBar && (
              <div className="d-flex flex-items-center flex-justify-between gap-2 mb-1">
                {reactionGroups && (
                  <ReactionViewerBase
                    ref={reactionButtonRef}
                    reactionGroups={reactionGroups}
                    onReact={onReact}
                    canReact={comment.viewerCanReact}
                  />
                )}
                {showFeedbackButtons && (
                  <AutomatedReviewFeedback
                    automatedCommentId={automatedCommentId}
                    basePath={basePath}
                    feedbackOptions={feedbackOptions}
                    showNegativeFeedbackModal={showNegativeFeedbackModal}
                  />
                )}
                {footnoteTextHTML || showAiNote ? (
                  <div className="text-small color-fg-muted">
                    {footnoteTextHTML ? (
                      <UnsafeHTMLText html={footnoteTextHTML} />
                    ) : (
                      'Copilot uses AI. Check for mistakes.'
                    )}
                  </div>
                ) : null}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

export function AutomatedReviewThreadCommentErrorFallback() {
  return (
    <div className={`position-relative`}>
      <Blankslate border>
        <Blankslate.Visual>
          <AlertIcon size={24} className="fgColor-muted mt-3 mb-3" />
        </Blankslate.Visual>
        <Blankslate.Heading>
          <strong>There was a problem loading this review comment</strong>
        </Blankslate.Heading>
        <div className="mb-n2">
          <Blankslate.Description>Refresh the page or try again later</Blankslate.Description>
        </div>
        <Blankslate.SecondaryAction href="https://www.githubstatus.com/">GitHub status</Blankslate.SecondaryAction>
      </Blankslate>
    </div>
  )
}
